import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { initializeApp } from "firebase/app";
import {
  getAuth,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  signOut,
} from "firebase/auth";
import AuthService from "../services/AuthService";
import BeatLoader from "react-spinners/BeatLoader";

// ==================================================
// FIREBASE CONFIG
// ==================================================

const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const firebaseConfig = JSON.parse(
  process.env.REACT_APP_FIREBASE_CONFIG || "{}",
);

let app;
let auth;

try {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
} catch (error) {
  console.error("❌ Firebase initialization failed:", error);
}

// ==================================================
// COUNTRY CODES
// ==================================================

const countryCodes = [
  { code: "+91", name: "India" },
  { code: "+1", name: "USA" },
  { code: "+44", name: "UK" },
  { code: "+61", name: "Australia" },
  { code: "+971", name: "UAE" },
  { code: "+966", name: "Saudi Arabia" },
  { code: "+974", name: "Qatar" },
  { code: "+965", name: "Kuwait" },
  { code: "+973", name: "Bahrain" },
  { code: "+968", name: "Oman" },
  { code: "+977", name: "Nepal" },
  { code: "+65", name: "Singapore" },
  { code: "+60", name: "Malaysia" },
  { code: "+66", name: "Thailand" },
  { code: "+256", name: "Uganda" },
  { code: "+234", name: "Nigeria" },
  { code: "+251", name: "Ethiopia" },
  { code: "+233", name: "Ghana" },
  { code: "+880", name: "Bangladesh" },
  { code: "+95", name: "Myanmar" },
  { code: "+231", name: "Liberia" },
  { code: "+218", name: "Libya" },
  { code: "+229", name: "Benin" },
  { code: "+7", name: "Russia" },
  { code: "+967", name: "Yemen" },
];

// ==================================================
// INPUT FIELD
// ==================================================

const InputField = ({
  name,
  type,
  placeholder,
  value,
  onChange,
  error,
  ...props
}) => (
  <div className="mb-3">
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`form-control ${error ? "is-invalid" : ""}`}
      required
      {...props}
    />

    {error && <div className="invalid-feedback">{error}</div>}
  </div>
);

// ==================================================
// FORGOT PASSWORD PAGE
// ==================================================

const ForgotPasswordPage = () => {
  const navigate = useNavigate();

  // --------------------------------------------------
  // STEP
  // 1 = Phone
  // 2 = OTP
  // 3 = New Password
  // --------------------------------------------------

  const [step, setStep] = useState(1);

  // --------------------------------------------------
  // FORM
  // --------------------------------------------------

  const [form, setForm] = useState({
    phone: "",
    countryCode: "+91",
    password: "",
    password_confirmation: "",
  });

  const [verificationCode, setVerificationCode] = useState("");

  // --------------------------------------------------
  // UI STATE
  // --------------------------------------------------

  const [message, setMessage] = useState({
    text: "",
    type: "error",
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // --------------------------------------------------
  // FIREBASE REFS
  // --------------------------------------------------

  const recaptchaVerifierRef = useRef(null);
  const confirmationResultRef = useRef(null);

  // ==================================================
  // INITIALIZE RECAPTCHA
  // ==================================================

  useEffect(() => {
    if (!auth) {
      setMessage({
        text: "Firebase authentication could not be initialized.",
        type: "error",
      });

      return;
    }

    try {
      recaptchaVerifierRef.current = new RecaptchaVerifier(
        auth,
        "recaptcha-container",
        {
          size: "invisible",
        },
      );
    } catch (error) {
      console.error("❌ reCAPTCHA initialization failed:", error);

      setMessage({
        text: "Unable to initialize phone verification.",
        type: "error",
      });
    }

    return () => {
      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear();
        } catch (error) {
          console.error("reCAPTCHA cleanup error:", error);
        }
      }
    };
  }, []);

  // ==================================================
  // HANDLE INPUT CHANGE
  // ==================================================

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }

    setMessage({
      text: "",
      type: "error",
    });
  };

  // ==================================================
  // VALIDATE PHONE
  // ==================================================

  const validatePhone = () => {
    const newErrors = {};

    const fullPhone = form.countryCode + form.phone.replace(/^\+/, "");

    if (!/^\+[1-9]\d{10,14}$/.test(fullPhone)) {
      newErrors.phone = "Please enter a valid phone number.";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  // ==================================================
  // SEND OTP
  // ==================================================

  const handleSendVerification = async (e) => {
    e.preventDefault();

    if (!validatePhone()) {
      return;
    }

    if (!auth) {
      setMessage({
        text: "Firebase authentication is not available.",
        type: "error",
      });

      return;
    }

    setIsLoading(true);

    setMessage({
      text: "",
      type: "error",
    });

    try {
      const fullPhone = form.countryCode + form.phone.replace(/^\+/, "");

      const verifier = recaptchaVerifierRef.current;

      if (!verifier) {
        throw new Error("reCAPTCHA verifier is not initialized.");
      }

      confirmationResultRef.current = await signInWithPhoneNumber(
        auth,
        fullPhone,
        verifier,
      );

      setMessage({
        text: "A verification code has been sent to your phone!",
        type: "success",
      });

      setStep(2);
    } catch (error) {
      console.error("Phone verification error:", error);

      let msg =
        "Failed to send verification code. Please check the number and try again.";

      if (error.code === "auth/invalid-phone-number") {
        msg = "The phone number format is not valid.";

        setErrors((prev) => ({
          ...prev,
          phone: msg,
        }));
      } else if (error.code === "auth/too-many-requests") {
        msg = "Too many requests. Please try again later.";
      } else if (error.code === "auth/captcha-check-failed") {
        msg = "reCAPTCHA verification failed. Please try again.";
      } else if (error.code === "auth/quota-exceeded") {
        msg = "SMS quota exceeded. Please try again later.";
      } else {
        msg = error.message || "Failed to send verification code.";
      }

      setMessage({
        text: msg,
        type: "error",
      });

      // Reset reCAPTCHA after failure.
      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear();
        } catch (clearError) {
          console.error(clearError);
        }

        recaptchaVerifierRef.current = null;
      }

      // Create a fresh verifier.
      if (auth) {
        try {
          recaptchaVerifierRef.current = new RecaptchaVerifier(
            auth,
            "recaptcha-container",
            {
              size: "invisible",
            },
          );
        } catch (recaptchaError) {
          console.error("Failed to recreate reCAPTCHA:", recaptchaError);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ==================================================
  // VERIFY OTP
  // ==================================================

  const handleVerifyCode = async (e) => {
    e.preventDefault();

    if (!verificationCode || verificationCode.length !== 6) {
      setMessage({
        text: "Please enter the 6-digit verification code.",
        type: "error",
      });

      return;
    }

    if (!confirmationResultRef.current) {
      setMessage({
        text: "Verification process expired. Please request a new code.",
        type: "error",
      });

      setStep(1);

      return;
    }

    setIsLoading(true);

    setMessage({
      text: "",
      type: "error",
    });

    try {
      // ----------------------------------------------
      // Firebase verifies OTP
      // ----------------------------------------------

      const credential =
        await confirmationResultRef.current.confirm(verificationCode);

      const user = credential.user;

      // ----------------------------------------------
      // Get Firebase ID token
      // ----------------------------------------------

      const idToken = await user.getIdToken();

      // ----------------------------------------------
      // Store token temporarily
      // ----------------------------------------------

      sessionStorage.setItem("password_reset_id_token", idToken);

      setMessage({
        text: "Phone number verified successfully.",
        type: "success",
      });

      setStep(3);
    } catch (error) {
      console.error("OTP verification error:", error);

      let msg = "Verification failed.";

      if (error.code === "auth/code-expired") {
        msg = "The verification code has expired. Please request a new one.";

        setStep(1);
      } else if (error.code === "auth/invalid-verification-code") {
        msg = "Invalid verification code. Please try again.";
      } else {
        msg = error.message || "Verification failed. Please try again.";
      }

      setMessage({
        text: msg,
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // ==================================================
  // RESET PASSWORD
  // ==================================================

  const handleResetPassword = async (e) => {
    e.preventDefault();

    setErrors({});

    // ----------------------------------------------
    // Validate password
    // ----------------------------------------------

    const newErrors = {};

    if (form.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters.";
    }

    if (form.password !== form.password_confirmation) {
      newErrors.password_confirmation = "Passwords do not match.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // ----------------------------------------------
    // Get Firebase token
    // ----------------------------------------------

    const idToken = sessionStorage.getItem("password_reset_id_token");

    if (!idToken) {
      setMessage({
        text: "Your verification session has expired. Please start again.",
        type: "error",
      });

      setStep(1);

      return;
    }

    setIsLoading(true);

    setMessage({
      text: "",
      type: "error",
    });

    try {
      // ----------------------------------------------
      // Send Firebase token + new password to Laravel
      // ----------------------------------------------
      await AuthService.resetPasswordWithPhone({
        id_token: idToken,
        password: form.password,
        password_confirmation: form.password_confirmation,
      });

      // ----------------------------------------------
      // Remove temporary token
      // ----------------------------------------------

      sessionStorage.removeItem("password_reset_id_token");

      // ----------------------------------------------
      // Firebase signInWithPhoneNumber also signs the
      // Firebase user in. Your application uses Laravel
      // authentication, so sign out from Firebase.
      // ----------------------------------------------

      try {
        await signOut(auth);
      } catch (firebaseError) {
        console.error("Firebase sign-out error:", firebaseError);
      }

      setMessage({
        text: "Password reset successfully! Redirecting to login...",
        type: "success",
      });

      // ----------------------------------------------
      // Redirect to login
      // ----------------------------------------------

      setTimeout(() => {
        navigate("/login");
      }, 1500);
    } catch (error) {
      console.error("Password reset error:", error);

      let msg = "Unable to reset your password. Please try again.";

      if (error.response?.status === 404) {
        msg =
          error.response.data?.message ||
          "No account was found with this phone number.";
      } else if (error.response?.status === 401) {
        msg =
          error.response.data?.message ||
          "Your verification session is invalid or expired.";

        sessionStorage.removeItem("password_reset_id_token");

        setStep(1);
      } else if (error.response?.status === 422) {
        msg =
          error.response.data?.message ||
          "Please check the information you entered.";

        if (error.response.data?.errors?.password) {
          msg = error.response.data.errors.password[0];
        }
      } else if (error.response?.data?.message) {
        msg = error.response.data.message;
      }

      setMessage({
        text: msg,
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // ==================================================
  // CHANGE PHONE NUMBER
  // ==================================================

  const handleChangePhone = () => {
    setStep(1);

    setVerificationCode("");

    setForm((prev) => ({
      ...prev,
      password: "",
      password_confirmation: "",
    }));

    setErrors({});

    setMessage({
      text: "",
      type: "error",
    });

    confirmationResultRef.current = null;

    sessionStorage.removeItem("password_reset_id_token");
  };

  // ==================================================
  // BACK TO LOGIN
  // ==================================================

  const handleBackToLogin = () => {
    sessionStorage.removeItem("password_reset_id_token");

    navigate("/login");
  };

  // ==================================================
  // RENDER
  // ==================================================

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
      <div
        className="card border-0 shadow-lg rounded-4 overflow-hidden w-100 position-relative"
        style={{ maxWidth: "420px" }}
      >
        {/* ==========================================
            LOADING OVERLAY
        ========================================== */}

        {isLoading && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center rounded-4 bg-white bg-opacity-90"
            style={{ zIndex: 10 }}
          >
            <BeatLoader color="#0d6efd" loading={true} size={18} />

            <div className="mt-2 text-muted">
              {step === 1 && "Sending verification code..."}

              {step === 2 && "Verifying your phone..."}

              {step === 3 && "Resetting your password..."}
            </div>
          </div>
        )}

        {/* ==========================================
            HEADER
        ========================================== */}

        <div className="card-header bg-white text-center py-4">
          <div className="mb-2">
            <i className="bi bi-shield-lock text-primary fs-1"></i>
          </div>

          <h2 className="h4 mb-1 fw-bold text-dark">
            {step === 1 && "Forgot Password?"}
            {step === 2 && "Verify Your Phone"}
            {step === 3 && "Create New Password"}
          </h2>

          <p className="text-muted mb-0">
            {step === 1 && "Reset your password using your mobile number"}

            {step === 2 &&
              `Enter the 6-digit code sent to ${form.countryCode}${form.phone}`}

            {step === 3 && "Choose a new password for your account"}
          </p>
        </div>

        {/* ==========================================
            BODY
        ========================================== */}

        <div className="card-body p-4">
          {/* ========================================
              MESSAGE
          ======================================== */}

          {message.text && (
            <div
              className={`alert ${
                message.type === "success" ? "alert-success" : "alert-danger"
              } d-flex align-items-center`}
              role="alert"
            >
              <i
                className={`bi ${
                  message.type === "success"
                    ? "bi-check-circle-fill"
                    : "bi-exclamation-circle-fill"
                } me-2`}
              ></i>

              <div>{message.text}</div>
            </div>
          )}

          {/* ========================================
              STEP 1
              PHONE NUMBER
          ======================================== */}

          {step === 1 && (
            <form onSubmit={handleSendVerification} noValidate>
              <div className="mb-3">
                <label className="form-label fw-semibold text-dark">
                  Mobile Number
                </label>

                <div className="input-group">
                  <select
                    name="countryCode"
                    value={form.countryCode}
                    onChange={handleChange}
                    className="form-select"
                    style={{ maxWidth: "120px" }}
                  >
                    {countryCodes.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.name} ({c.code})
                      </option>
                    ))}
                  </select>

                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="Phone Number"
                    className={`form-control ${
                      errors.phone ? "is-invalid" : ""
                    }`}
                    required
                  />

                  {errors.phone && (
                    <div className="invalid-feedback w-100">{errors.phone}</div>
                  )}
                </div>

                <div className="form-text text-muted">
                  Enter the mobile number linked to your account.
                </div>
              </div>

              <button
                type="submit"
                className="btn btn-primary w-100 py-2 mb-3 fw-semibold"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    ></span>
                    Sending Code...
                  </>
                ) : (
                  <>
                    <i className="bi bi-send me-2"></i>
                    Send Verification Code
                  </>
                )}
              </button>
            </form>
          )}

          {/* ========================================
              STEP 2
              OTP
          ======================================== */}

          {step === 2 && (
            <form onSubmit={handleVerifyCode} noValidate>
              <div className="mb-3">
                <label className="form-label fw-semibold text-dark">
                  Verification Code
                </label>

                <input
                  type="text"
                  name="verificationCode"
                  value={verificationCode}
                  onChange={(e) =>
                    setVerificationCode(
                      e.target.value.replace(/\D/g, "").slice(0, 6),
                    )
                  }
                  placeholder="######"
                  className="form-control text-center fs-5"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength="6"
                  autoComplete="one-time-code"
                  required
                />

                <div className="form-text text-muted">
                  Enter the 6-digit code sent to{" "}
                  <strong>
                    {form.countryCode}
                    {form.phone}
                  </strong>
                </div>
              </div>

              <button
                type="submit"
                className="btn btn-success w-100 py-2 mb-2 fw-semibold"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    ></span>
                    Verifying...
                  </>
                ) : (
                  <>
                    <i className="bi bi-shield-check me-2"></i>
                    Verify Phone
                  </>
                )}
              </button>

              <button
                type="button"
                className="btn btn-link w-100 text-decoration-none"
                onClick={handleChangePhone}
                disabled={isLoading}
              >
                <i className="bi bi-arrow-left me-1"></i>
                Change Phone Number
              </button>
            </form>
          )}

          {/* ========================================
              STEP 3
              NEW PASSWORD
          ======================================== */}

          {step === 3 && (
            <form onSubmit={handleResetPassword} noValidate>
              <InputField
                name="password"
                type="password"
                placeholder="New Password"
                value={form.password}
                onChange={handleChange}
                error={errors.password}
                autoComplete="new-password"
              />

              <InputField
                name="password_confirmation"
                type="password"
                placeholder="Confirm New Password"
                value={form.password_confirmation}
                onChange={handleChange}
                error={errors.password_confirmation}
                autoComplete="new-password"
              />

              <div className="form-text text-muted mb-3">
                Password must be at least 6 characters.
              </div>

              <button
                type="submit"
                className="btn btn-primary w-100 py-2 mb-3 fw-semibold"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    ></span>
                    Resetting...
                  </>
                ) : (
                  <>
                    <i className="bi bi-key me-2"></i>
                    Reset Password
                  </>
                )}
              </button>
            </form>
          )}

          {/* ========================================
              BACK TO LOGIN
          ======================================== */}

          <div className="text-center mt-4 pt-3 border-top">
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={handleBackToLogin}
              disabled={isLoading}
            >
              <i className="bi bi-arrow-left me-2"></i>
              Back to Login
            </button>
          </div>

          {/* ========================================
              FIREBASE RECAPTCHA
          ======================================== */}

          <div id="recaptcha-container" className="mt-2"></div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
